import React, { createContext, useContext } from 'react';
import type { SlotProps } from './interface/slotProps';
import { HoldAction } from './molecules/HoldAction';
import { PatientInfo } from './molecules/PatientInfo';

/**
 * The injectable `componentId` -> component map the slot-rendering component (`Slot`) resolves
 * against. Two ready-made registries are exported below: `EMPTY_SLOT_REGISTRY` (an empty, frozen,
 * null-prototype map -- also `useSlotRegistry()`'s context default) and `DEFAULT_SLOT_REGISTRY` (the
 * real in-folder production registry -- as of CDMX-18402 it holds `PatientInfo`, and as of CDMX-18412
 * it additionally holds `HoldAction` (the coding bench's Hold action slot), each imported by its own
 * deep molecule path below; every future real in-folder component registers into it the same way).
 * `SlotRegistryProvider`'s `registry` prop still lets a caller (Storybook, a test, or a future
 * dashboard-ui-side adapter) inject a DIFFERENT registry -- real or fake -- in place of either
 * default; `EMPTY_SLOT_REGISTRY` in particular remains useful for a test that specifically wants to
 * exercise `Slot`'s "not found" path. Isolation: this file imports `react`, the `SlotProps` type
 * (deep path, not the `./interface` barrel), `PatientInfo` (deep path, `./molecules/PatientInfo`),
 * and `HoldAction` (deep path, `./molecules/HoldAction`) -- nothing else from `project_starburst` or
 * beyond; neither `PatientInfo` nor `HoldAction` imports anything back from this file (no cycle --
 * see each component's own isolation note), and the boundary guard
 * (`__tests__/boundary.test.ts`) verifies the whole tree stays in-bounds regardless.
 */
export type SlotRegistry = Record<string, React.FC<SlotProps>>;

/**
 * Empty + immutable + null-prototype. `Object.create(null)` means the object has NO inherited
 * members (no `constructor`/`toString`/`__proto__`) for `resolveSlot`'s own-key check to ever have
 * to reject, and `Object.freeze` blocks any accidental mutation of this shared production
 * singleton at runtime.
 */
export const EMPTY_SLOT_REGISTRY: Readonly<SlotRegistry> = Object.freeze(
  Object.create(null)
);

/**
 * The in-folder production registry of real slot components the shell resolves `componentId`s
 * against. `PatientInfo` (CDMX-18402) is the FIRST entry -- the framework base shipped this empty;
 * `HoldAction` (CDMX-18412) is the SECOND real registered component -- an ACTION slot (the coding
 * bench's Hold trigger button), rather than a data-display slot like `PatientInfo`, but registered
 * the exact same way: every future real in-folder component registers here the same way (one more
 * own key on this same object, deep-path-imported from its own molecule folder -- never imported BY
 * the component it registers, so registering a component here can never create an import cycle).
 * Null-prototype + frozen for the same own-key-lookup safety as `EMPTY_SLOT_REGISTRY` --
 * `Object.assign` onto a fresh `Object.create(null)` copies OWN enumerable keys from the source
 * object literal without ever touching the target's (null) prototype, so the result is still
 * exactly as safe against a `constructor`/`__proto__`/`toString` collision as the empty singleton
 * above.
 */
export const DEFAULT_SLOT_REGISTRY: Readonly<SlotRegistry> = Object.freeze(
  Object.assign(Object.create(null), { PatientInfo, HoldAction })
);

/**
 * Own-key lookup into `registry`, mirroring `slots/readState.ts`'s own-key rationale exactly:
 * `Object.prototype.hasOwnProperty.call(...)`, NOT the `in` operator or a plain `registry[id]`
 * read, so inherited keys (`constructor`, `__proto__`, `toString`, ...) resolve to `undefined`
 * instead of resolving through the prototype chain -- `componentId` is a config-driven string
 * (`Slot.componentId`) that must never be trusted to safely index via `in`. Returns `undefined`
 * (never throws) when the key is absent -- the `Slot` component is what turns an `undefined`
 * resolve into the visible "Component not found!" error, not this function.
 */
export function resolveSlot(
  registry: SlotRegistry,
  componentId: string
): React.FC<SlotProps> | undefined {
  // biome-ignore lint/suspicious/noPrototypeBuiltins: task-3 brief pins this exact own-key form verbatim (matches slots/readState.ts's established suppression)
  return Object.prototype.hasOwnProperty.call(registry, componentId)
    ? registry[componentId]
    : undefined;
}

const Ctx = createContext<SlotRegistry>(EMPTY_SLOT_REGISTRY);

/**
 * Defaults to `EMPTY_SLOT_REGISTRY` when no `registry` prop is given -- production code never
 * passes one today (no call site exists yet; a future dashboard-ui-side adapter or Storybook
 * decorator injects fakes). Mirrors `interface/context.tsx`'s `CodingScreenProvider` shape.
 */
export const SlotRegistryProvider: React.FC<
  React.PropsWithChildren<{ registry?: SlotRegistry }>
> = ({ registry = EMPTY_SLOT_REGISTRY, children }) => (
  <Ctx.Provider value={registry}>{children}</Ctx.Provider>
);

/** Convenience accessor -- resolves to `EMPTY_SLOT_REGISTRY` outside any `SlotRegistryProvider`. */
export const useSlotRegistry = (): SlotRegistry => useContext(Ctx);
